import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  groupAttachmentsByMonth,
  partitionAttachments,
  type AttachmentMonthGroup,
  type DocAttachmentItem,
  type LinkAttachmentItem,
  type MediaAttachmentItem,
  type RawConversationMessage,
} from "@/lib/conversation-attachments";

const MESSAGE_LIMIT = 500;

export interface LeadConversationAttachmentsResult {
  media: MediaAttachmentItem[];
  links: LinkAttachmentItem[];
  docs: DocAttachmentItem[];
  mediaGroups: AttachmentMonthGroup<MediaAttachmentItem>[];
  linkGroups: AttachmentMonthGroup<LinkAttachmentItem>[];
  docGroups: AttachmentMonthGroup<DocAttachmentItem>[];
  counts: { media: number; links: number; docs: number; total: number };
  loading: boolean;
  hasConversations: boolean;
  error: string | null;
  refetch: () => void;
}

export function useLeadConversationAttachments(
  leadId: string | null,
  enabled: boolean,
): LeadConversationAttachmentsResult {
  const [media, setMedia] = useState<MediaAttachmentItem[]>([]);
  const [links, setLinks] = useState<LinkAttachmentItem[]>([]);
  const [docs, setDocs] = useState<DocAttachmentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasConversations, setHasConversations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!enabled || !leadId) {
      setMedia([]);
      setLinks([]);
      setDocs([]);
      setHasConversations(false);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const { data: conversations, error: convError } = await supabase
          .from("conversations")
          .select("id")
          .eq("lead_id", leadId);

        if (convError) throw convError;
        if (cancelled) return;

        const conversationIds = (conversations ?? []).map((c) => c.id);
        setHasConversations(conversationIds.length > 0);

        if (conversationIds.length === 0) {
          setMedia([]);
          setLinks([]);
          setDocs([]);
          setLoading(false);
          return;
        }

        const { data: messages, error: msgError } = await supabase
          .from("messages")
          .select("id, body, from_user, created_at, conversation_id, metadata")
          .in("conversation_id", conversationIds)
          .is("deleted_at", null)
          .order("created_at", { ascending: false })
          .limit(MESSAGE_LIMIT);

        if (msgError) throw msgError;
        if (cancelled) return;

        const raw: RawConversationMessage[] = (messages ?? []).map((m) => ({
          id: m.id,
          body: m.body,
          from_user: m.from_user,
          created_at: m.created_at ?? new Date().toISOString(),
          conversation_id: m.conversation_id,
          metadata: (m.metadata as Record<string, unknown> | null) ?? null,
        }));

        const partitioned = partitionAttachments(raw);
        setMedia(partitioned.media);
        setLinks(partitioned.links);
        setDocs(partitioned.docs);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Erro ao carregar anexos");
          setMedia([]);
          setLinks([]);
          setDocs([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [leadId, enabled, tick]);

  const counts = {
    media: media.length,
    links: links.length,
    docs: docs.length,
    total: media.length + links.length + docs.length,
  };

  return {
    media,
    links,
    docs,
    mediaGroups: groupAttachmentsByMonth(media),
    linkGroups: groupAttachmentsByMonth(links),
    docGroups: groupAttachmentsByMonth(docs),
    counts,
    loading,
    hasConversations,
    error,
    refetch,
  };
}
